import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { getVertical } from "@/data/verticals";
import ListingCard from "@/components/ListingCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, SlidersHorizontal, Frown, PlusCircle } from "lucide-react";
import { V_ICONS } from "@/lib/verticalIcons";
import { INDIA_STATES } from "@/data/indiaLocations";

const ANY = "any";

export default function VerticalListings() {
  const { vertical } = useParams();
  const navigate = useNavigate();
  const config = getVertical(vertical);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selects, setSelects] = useState({});
  const [state, setState] = useState(ANY);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [q, setQ] = useState("");

  const fetchData = useCallback(async () => {
    if (!config) return;
    setLoading(true);
    const params = {};
    Object.entries(selects).forEach(([k, v]) => { if (v && v !== ANY) params[k] = v; });
    if (state !== ANY) params.state = state;
    if (minPrice) params.min_price = minPrice;
    if (maxPrice) params.max_price = maxPrice;
    if (q) params.q = q;
    const res = await api.get(`/listings/${vertical}`, { params });
    setItems(res.data);
    setLoading(false);
  }, [config, vertical, selects, state, minPrice, maxPrice, q]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // reset local filter state when switching vertical
  useEffect(() => { setSelects({}); setState(ANY); setMinPrice(""); setMaxPrice(""); setQ(""); }, [vertical]);

  if (!config) return <Navigate to="/" replace />;
  const Icon = V_ICONS[config.icon];

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-gold-dark flex items-center gap-1.5">
            <Icon className="h-3.5 w-3.5" /> VYAPAAR {config.label}
          </p>
          <h1 className="mt-1.5 font-display text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">{config.heading}</h1>
        </div>
        <Button data-testid="vertical-sell-btn" onClick={() => navigate(`/v/${vertical}/post`)} className="bg-gold hover:bg-gold-dark text-navy font-semibold">
          <PlusCircle className="h-4 w-4 mr-1.5" /> {config.sellCta}
        </Button>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-4 text-slate-700">
          <SlidersHorizontal className="h-4 w-4" /><span className="text-sm font-semibold">Filters</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {config.filters.map((f) => (
            <Select key={f.param} value={selects[f.param] || ANY} onValueChange={(v) => setSelects((p) => ({ ...p, [f.param]: v }))}>
              <SelectTrigger data-testid={`filter-${f.param}`}><SelectValue placeholder={f.label} /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value={ANY}>All {f.label}</SelectItem>
                {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          ))}
          <Select value={state} onValueChange={setState}>
            <SelectTrigger data-testid="filter-state"><SelectValue placeholder="State/UT" /></SelectTrigger>
            <SelectContent className="max-h-72"><SelectItem value={ANY}>All States/UTs</SelectItem>{INDIA_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="filter-min-price" type="number" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchData()} placeholder="Min ₹" />
          <Input data-testid="filter-max-price" type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchData()} placeholder="Max ₹" />
          <div className="flex gap-2 col-span-2 md:col-span-1 lg:col-span-1">
            <Input data-testid="vertical-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchData()} placeholder="Search…" />
            <Button data-testid="vertical-search-btn" onClick={fetchData} className="bg-navy hover:bg-navy-dark shrink-0"><Search className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      <p className="mt-6 mb-4 text-sm text-slate-500" data-testid="vertical-count">{loading ? "Loading…" : `${items.length} ${config.emptyLabel} found`}</p>

      {!loading && items.length === 0 ? (
        <div className="py-24 text-center"><Frown className="h-12 w-12 text-slate-300 mx-auto" /><p className="mt-4 text-slate-500">No {config.emptyLabel} match your filters.</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((it, i) => <ListingCard key={it.id} listing={it} config={config} index={i} />)}
        </div>
      )}
    </div>
  );
}
